import React, { useState } from 'react';
import { ActiveEffect } from '../../types';

export interface ActiveEffectsPanelProps {
  /** List of active effects on the character */
  effects: ActiveEffect[];
  /** Whether effects can be edited (own character) */
  isEditable: boolean;
  /** Callback when an effect is added */
  onAddEffect?: (effect: ActiveEffect) => void;
  /** Callback when an effect is removed */
  onRemoveEffect?: (index: number) => void;
  /** Callback when an effect's duration is updated */
  onUpdateDuration?: (index: number, duration: number) => void;
  /** Optional className for additional styling */
  className?: string;
}

// Effect type configurations with icons, colors, and descriptions
const EFFECT_CONFIG: Record<string, { 
  icon: string; 
  color: string; 
  bgColor: string; 
  borderColor: string;
  description: string;
  category: 'debuff' | 'buff' | 'control';
}> = {
  poisoned: { 
    icon: '☠️', 
    color: 'text-green-400', 
    bgColor: 'bg-green-900/30', 
    borderColor: 'border-green-600/40',
    description: 'Takes damage over time',
    category: 'debuff'
  },
  cursed: { 
    icon: '💀', 
    color: 'text-purple-400', 
    bgColor: 'bg-purple-900/30', 
    borderColor: 'border-purple-600/40',
    description: 'Suffers from a dark curse',
    category: 'debuff'
  },
  bound: { 
    icon: '⛓️', 
    color: 'text-gray-300', 
    bgColor: 'bg-gray-700/30', 
    borderColor: 'border-gray-500/40',
    description: 'Movement restricted',
    category: 'control'
  },
  burning: { 
    icon: '🔥', 
    color: 'text-orange-400', 
    bgColor: 'bg-orange-900/30', 
    borderColor: 'border-orange-600/40',
    description: 'On fire, taking burn damage',
    category: 'debuff'
  },
  frozen: { 
    icon: '❄️', 
    color: 'text-blue-400', 
    bgColor: 'bg-blue-900/30', 
    borderColor: 'border-blue-600/40',
    description: 'Frozen in place',
    category: 'control'
  },
  stunned: { 
    icon: '💫', 
    color: 'text-yellow-400', 
    bgColor: 'bg-yellow-900/30', 
    borderColor: 'border-yellow-600/40',
    description: 'Cannot take actions',
    category: 'control'
  },
  blessed: { 
    icon: '✨', 
    color: 'text-amber-400', 
    bgColor: 'bg-amber-900/30', 
    borderColor: 'border-amber-600/40',
    description: 'Divine protection',
    category: 'buff'
  },
  shielded: { 
    icon: '🛡️', 
    color: 'text-cyan-400', 
    bgColor: 'bg-cyan-900/30', 
    borderColor: 'border-cyan-600/40',
    description: 'Protected by a shield',
    category: 'buff'
  },
  weakened: { 
    icon: '📉', 
    color: 'text-red-400', 
    bgColor: 'bg-red-900/30', 
    borderColor: 'border-red-600/40',
    description: 'Reduced effectiveness',
    category: 'debuff'
  },
  strengthened: { 
    icon: '📈', 
    color: 'text-emerald-400', 
    bgColor: 'bg-emerald-900/30', 
    borderColor: 'border-emerald-600/40',
    description: 'Increased power',
    category: 'buff'
  },
};

const DEFAULT_EFFECT_CONFIG = { 
  icon: '⚡', 
  color: 'text-gray-300', 
  bgColor: 'bg-gray-700/30', 
  borderColor: 'border-gray-500/40',
  description: 'Unknown effect',
  category: 'debuff' as const
};

const EFFECT_TYPES = Object.keys(EFFECT_CONFIG);

/**
 * ActiveEffectsPanel Component
 * 
 * Modern, intuitive panel for managing active effects with:
 * - Card-based effect display with icons and descriptions
 * - Duration controls with increment/decrement buttons
 * - Categorized effect selection in modal
 * - Visual distinction between buffs, debuffs, and control effects
 * 
 * Requirements: 3.5, 4.8
 */
export const ActiveEffectsPanel: React.FC<ActiveEffectsPanelProps> = ({
  effects,
  isEditable,
  onAddEffect,
  onRemoveEffect,
  onUpdateDuration,
  className = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newEffectType, setNewEffectType] = useState<string>(EFFECT_TYPES[0]);
  const [newEffectDuration, setNewEffectDuration] = useState<number>(3);
  const [newEffectValue, setNewEffectValue] = useState<string>('');

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setNewEffectType(EFFECT_TYPES[0]);
    setNewEffectDuration(3);
    setNewEffectValue('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleAddEffect = () => {
    const effect: ActiveEffect = {
      type: newEffectType,
      duration: newEffectDuration,
      value: newEffectValue ? parseInt(newEffectValue, 10) : undefined,
    };
    onAddEffect?.(effect);
    handleCloseModal();
  };

  const handleRemoveEffect = (index: number) => {
    onRemoveEffect?.(index);
  };

  const handleDurationChange = (index: number, delta: number) => {
    const effect = effects[index];
    if (effect && effect.duration !== -1) {
      const newDuration = Math.max(0, effect.duration + delta);
      onUpdateDuration?.(index, newDuration);
    }
  };

  const getEffectConfig = (type: string) => {
    return EFFECT_CONFIG[type.toLowerCase()] || DEFAULT_EFFECT_CONFIG;
  };

  return (
    <div className={`active-effects-panel bg-gray-800/50 rounded-xl p-5 border border-gray-700 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-lg font-semibold text-white">Status Effects</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {effects.length === 0 ? 'No active effects' : `${effects.length} active effect${effects.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {isEditable && onAddEffect && (
          <button
            type="button"
            onClick={handleOpenModal}
            className="
              px-4 py-2.5 text-sm font-semibold
              bg-gradient-to-b from-primary-500 to-primary-600 
              hover:from-primary-400 hover:to-primary-500
              text-white
              rounded-xl transition-all duration-200
              shadow-sm hover:shadow-md active:scale-95
              flex items-center gap-2
            "
            aria-label="Add new effect"
          >
            <span className="text-lg leading-none">+</span>
            Add Effect
          </button>
        )}
      </div>

      {/* Effects List */}
      {effects.length > 0 ? (
        <div className="space-y-4" data-testid="effects-list">
          {effects.map((effect, index) => {
            const config = getEffectConfig(effect.type);
            const isPermanent = effect.duration === -1;
            
            return (
              <div
                key={`${effect.type}-${index}`}
                className={`
                  effect-card
                  ${config.bgColor} ${config.borderColor}
                  border-2 rounded-xl p-4
                  transition-all duration-200
                  shadow-sm hover:shadow-md
                `}
                data-testid={`effect-card-${index}`}
                data-effect-type={effect.type}
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Effect Info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-2xl flex-shrink-0">{config.icon}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold capitalize ${config.color}`}>
                          {effect.type}
                        </span>
                        {effect.value !== undefined && effect.value !== null && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${config.bgColor} ${config.color} border ${config.borderColor}`}>
                            {effect.value > 0 ? `+${effect.value}` : effect.value}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{config.description}</p>
                    </div>
                  </div>

                  {/* Duration & Controls */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Duration Display/Controls */}
                    <div className="flex items-center gap-1.5">
                      {isPermanent ? (
                        <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${config.bgColor} ${config.color} border ${config.borderColor}`}>
                          ∞ Permanent
                        </span>
                      ) : (
                        <>
                          {isEditable && onUpdateDuration && (
                            <button
                              type="button"
                              onClick={() => handleDurationChange(index, -1)}
                              disabled={effect.duration <= 0}
                              className={`
                                w-8 h-8 rounded-lg flex items-center justify-center
                                text-sm font-bold transition-all duration-200
                                shadow-sm hover:shadow active:scale-95
                                ${effect.duration > 0
                                  ? 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white border border-red-400/50'
                                  : 'bg-gray-700/50 text-gray-500 border border-gray-600 cursor-not-allowed'
                                }
                              `}
                              aria-label="Decrease duration"
                            >
                              -
                            </button>
                          )}
                          <span className={`
                            px-3 py-1.5 min-w-[55px] text-center rounded-lg text-sm font-bold
                            bg-gray-900/60 border-2 ${config.borderColor} ${config.color}
                          `}>
                            {effect.duration}t
                          </span>
                          {isEditable && onUpdateDuration && (
                            <button
                              type="button"
                              onClick={() => handleDurationChange(index, 1)}
                              className="
                                w-8 h-8 rounded-lg flex items-center justify-center
                                text-sm font-bold transition-all duration-200
                                shadow-sm hover:shadow active:scale-95
                                bg-gradient-to-b from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white border border-green-400/50
                              "
                              aria-label="Increase duration"
                            >
                              +
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Remove Button */}
                    {isEditable && onRemoveEffect && (
                      <button
                        type="button"
                        onClick={() => handleRemoveEffect(index)}
                        className="
                          w-8 h-8 rounded-lg flex items-center justify-center
                          text-gray-400 hover:text-white
                          bg-gray-700/50 hover:bg-red-500
                          border border-gray-600 hover:border-red-400
                          transition-all duration-200
                          shadow-sm hover:shadow active:scale-95
                        "
                        title="Remove effect"
                        aria-label={`Remove ${effect.type} effect`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div 
          className="text-center py-10 border-2 border-dashed border-gray-600 rounded-xl bg-gray-900/30"
          data-testid="no-effects-message"
        >
          <span className="text-4xl mb-3 block">✨</span>
          <p className="text-sm text-gray-400 font-medium">No active effects</p>
          {isEditable && onAddEffect && (
            <p className="text-xs text-gray-500 mt-1">Click "Add Effect" to apply status effects</p>
          )}
        </div>
      )}

      {/* Add Effect Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={handleCloseModal}
          data-testid="add-effect-modal"
        >
          <div
            className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Add Status Effect</h3>
              <button
                type="button"
                onClick={handleCloseModal}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className="space-y-5">
              {/* Effect Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Effect Type
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-1">
                  {EFFECT_TYPES.map((type) => {
                    const config = EFFECT_CONFIG[type];
                    const isSelected = newEffectType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setNewEffectType(type)}
                        className={`
                          flex items-center gap-2 p-2 rounded-lg text-left
                          border transition-all duration-150
                          ${isSelected
                            ? `${config.bgColor} ${config.borderColor} ${config.color}`
                            : 'bg-gray-700/50 border-gray-600 text-gray-300 hover:bg-gray-700'
                          }
                        `}
                      >
                        <span className="text-lg">{config.icon}</span>
                        <span className="capitalize text-sm font-medium">{type}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Duration (turns)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setNewEffectDuration(Math.max(-1, newEffectDuration - 1))}
                    className="w-11 h-11 rounded-xl bg-gradient-to-b from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold transition-all duration-200 shadow-sm hover:shadow active:scale-95"
                  >
                    -
                  </button>
                  <div className="flex-1 px-4 py-2.5 bg-gray-900/80 border-2 border-gray-600 rounded-xl text-white text-center font-bold text-lg shadow-inner">
                    {newEffectDuration === -1 ? '∞' : newEffectDuration}
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewEffectDuration(Math.min(99, newEffectDuration + 1))}
                    className="w-11 h-11 rounded-xl bg-gradient-to-b from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-bold transition-all duration-200 shadow-sm hover:shadow active:scale-95"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewEffectDuration(-1)}
                    className={`
                      px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow active:scale-95
                      ${newEffectDuration === -1
                        ? 'bg-gradient-to-b from-primary-500 to-primary-600 text-white'
                        : 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600'
                      }
                    `}
                    title="Set as permanent"
                  >
                    ∞
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Use ∞ for permanent effects that don't expire
                </p>
              </div>

              {/* Value (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Value <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="number"
                  value={newEffectValue}
                  onChange={(e) => setNewEffectValue(e.target.value)}
                  placeholder="e.g., damage per turn, bonus amount"
                  className="
                    w-full px-4 py-2
                    bg-gray-700 border border-gray-600 rounded-lg
                    text-white placeholder:text-gray-500
                    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  "
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={handleCloseModal}
                className="
                  flex-1 px-4 py-3 rounded-xl font-semibold
                  bg-gray-700 hover:bg-gray-600 text-gray-300
                  border border-gray-600
                  transition-all duration-200
                  shadow-sm hover:shadow active:scale-[0.98]
                "
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddEffect}
                className="
                  flex-1 px-4 py-3 rounded-xl font-semibold
                  bg-gradient-to-b from-primary-500 to-primary-600 
                  hover:from-primary-400 hover:to-primary-500 
                  text-white
                  transition-all duration-200
                  shadow-sm hover:shadow-md active:scale-[0.98]
                "
              >
                Add Effect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
